import { useState, useEffect } from 'react';
import { Trophy, Loader2, ChevronRight, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getKnockout, getGroups, getTeams, getSettings, getThirdsStandings } from '../lib/api';
import { cn } from '../lib/utils';

const TeamBadge = ({ team, isWinner, size = 'md' }) => {
  const sizeClasses = {
    sm: 'text-xs py-1 px-2',
    md: 'text-sm py-2 px-3',
    lg: 'text-base py-3 px-4'
  };
  
  return (
    <div className={cn(
      "rounded-lg font-semibold truncate",
      sizeClasses[size],
      isWinner ? "bg-primary/20 text-primary border border-primary/30" : "bg-slate-800/50 text-slate-300"
    )}>
      {team?.name || 'TBD'}
    </div>
  );
};

const MatchBracket = ({ match, showLeg = true }) => {
  if (!match) return null;
  
  const homeTeam = match.home_team;
  const awayTeam = match.away_team;
  
  return (
    <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-3">
      {showLeg && match.leg && (
        <div className="text-[10px] text-slate-500 mb-2 uppercase tracking-wide">
          {match.leg === 1 ? 'Andata' : 'Ritorno'}
        </div>
      )}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <TeamBadge team={homeTeam} size="sm" />
          <span className="font-numbers text-lg w-8 text-center">
            {match.is_played ? match.home_score : '-'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TeamBadge team={awayTeam} size="sm" />
          <span className="font-numbers text-lg w-8 text-center">
            {match.is_played ? match.away_score : '-'}
          </span>
        </div>
      </div>
      {!match.is_played && (
        <div className="text-[10px] text-slate-500 mt-2">
          {new Date(match.match_date).toLocaleDateString('it-IT', {
            day: 'numeric', month: 'short', timeZone: 'UTC'
          })}
        </div>
      )}
    </div>
  );
};

export const TabellonePage = () => {
  const [knockout, setKnockout] = useState(null);
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [settings, setSettings] = useState(null);
  const [thirds, setThirds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [knockoutRes, groupsRes, teamsRes, settingsRes, thirdsRes] = await Promise.all([
          getKnockout().catch(() => ({ data: { quarterfinals: [], semifinals: [], final_3_4: null, final_1_2: null } })),
          getGroups(),
          getTeams(),
          getSettings().catch(() => ({ data: null })),
          getThirdsStandings().catch(() => ({ data: [] }))
        ]);
        setKnockout(knockoutRes.data);
        setGroups(groupsRes.data);
        setTeams(teamsRes.data);
        setSettings(settingsRes.data);
        setThirds(thirdsRes.data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group matches by match_number
  const groupByMatchNumber = (matches) => {
    const grouped = {};
    for (const match of matches || []) {
      const num = match.match_number || 0;
      if (!grouped[num]) grouped[num] = { leg1: null, leg2: null };
      if (match.leg === 1) grouped[num].leg1 = match;
      else if (match.leg === 2) grouped[num].leg2 = match;
    }
    return grouped;
  };

  const qfGroups = groupByMatchNumber(knockout?.quarterfinals);
  const sfGroups = groupByMatchNumber(knockout?.semifinals);

  // Build a PREVIEW bracket from current standings when knockout has not been generated yet.
  // Determina i nomi delle squadre in base alle classifiche live (1°/2°/3° per ogni girone)
  // e applica i pairings fissi (QF1=1°C vs 2°A, QF2=1°A vs 1ª best3°, QF3=1°B vs 2ª best3°, QF4=2°B vs 2°C).
  const buildPreviewBracket = () => {
    const standingsByGroup = {};
    for (const g of groups) {
      standingsByGroup[g.name] = teams
        .filter(t => t.group_id === g.id)
        .sort((a, b) =>
          (b.points || 0) - (a.points || 0) ||
          (b.goal_difference || 0) - (a.goal_difference || 0) ||
          (b.goals_for || 0) - (a.goals_for || 0) ||
          (a.fair_play || 0) - (b.fair_play || 0)
        );
    }
    const firstOf = (g) => standingsByGroup[g]?.[0] || null;
    const secondOf = (g) => standingsByGroup[g]?.[1] || null;
    const bestThirds = thirds.filter(t => t.qualifies).slice(0, 2);
    const t1 = bestThirds[0] ? teams.find(t => t.id === bestThirds[0].id) : null;
    const t2 = bestThirds[1] ? teams.find(t => t.id === bestThirds[1].id) : null;
    return [
      { num: 1, home: firstOf('C'), away: secondOf('A'), hint: '1°C vs 2°A' },
      { num: 2, home: firstOf('A'), away: t1, hint: '1°A vs 1ª miglior 3ª' },
      { num: 3, home: firstOf('B'), away: t2, hint: '1°B vs 2ª miglior 3ª' },
      { num: 4, home: secondOf('B'), away: secondOf('C'), hint: '2°B vs 2°C' },
    ];
  };

  // Get qualified teams from groups (top 2 + third place)
  const getQualifiedTeams = () => {
    const qualified = [];
    for (const group of groups) {
      const groupTeams = teams
        .filter(t => t.group_id === group.id)
        .sort((a, b) => (b.points || 0) - (a.points || 0) || (b.goal_difference || 0) - (a.goal_difference || 0));
      if (groupTeams.length >= 3) {
        qualified.push({
          group: group.name,
          first: groupTeams[0],
          second: groupTeams[1],
          third: groupTeams[2]
        });
      }
    }
    return qualified;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const hasKnockout = knockout && (
    knockout.quarterfinals?.length > 0 ||
    knockout.semifinals?.length > 0 ||
    knockout.final_3_4 ||
    knockout.final_1_2
  );

  const qualifiedTeams = getQualifiedTeams();
  // Evidenza in tempo reale: basta una partita giocata
  const groupPhaseComplete = teams.some(t => (t.played || 0) > 0);

  // Date dalla match_schedule (per accoppiamenti pre-sorteggio)
  const schedule = settings?.match_schedule || [];
  const findDate = (re) => {
    const e = schedule.find(s => re.test((s.label || '').toLowerCase()));
    return e ? e.date : null;
  };
  const fmtD = (d) => d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '—';
  // Accoppiamenti FISSI per girone letterale (A/B/C). Il sorteggio dei 2 gironi nelle settings
  // viene mantenuto solo come cerimonia e non incide più sull'accoppiamento.
  const drawDone = !!(settings?.knockout_draw_first_group && settings?.knockout_draw_second_group);

  const dateBlocks = [
    { label: `QF1 · 1°C vs 2°A`, and: findDate(/^1.*quarto.*andata/), rit: findDate(/^1.*quarto.*ritorno/) },
    { label: `QF2 · 1°A vs 1ª miglior 3ª`, and: findDate(/^1.*quarto.*andata/), rit: findDate(/^1.*quarto.*ritorno/) },
    { label: `QF3 · 1°B vs 2ª miglior 3ª`, and: findDate(/^2.*quarto.*andata/), rit: findDate(/^2.*quarto.*ritorno/) },
    { label: `QF4 · 2°B vs 2°C`, and: findDate(/^2.*quarto.*andata/), rit: findDate(/^2.*quarto.*ritorno/) },
  ];
  const sfDate = { and: findDate(/semifinal.*andata/), rit: findDate(/semifinal.*ritorno/) };
  const finaleDate = findDate(/^finale$/);

  return (
    <div className="min-h-screen py-8 px-4" data-testid="tabellone-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            TABELLONE <span className="text-primary">TORNEO</span>
          </h1>
          <p className="text-slate-400">Fase ad eliminazione diretta</p>
        </div>

        {/* Schema accoppiamenti + date programmate */}
        <Card className="bg-slate-900 border-slate-800 mb-8" data-testid="bracket-rules">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" /> Schema Quarti / Semifinali / Finali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Quarti di finale (andata + ritorno)</p>
              <ul className="space-y-2 text-sm">
                {dateBlocks.map((b, i) => (
                  <li key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2 rounded bg-slate-800/40 border border-slate-700/50">
                    <span className="font-medium">{b.label}</span>
                    <span className="text-xs text-slate-400 font-numbers shrink-0">
                      A: {fmtD(b.and)} · R: {fmtD(b.rit)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded bg-slate-800/40 border border-slate-700/50">
                <p className="text-xs text-slate-400 uppercase mb-1">Semifinali</p>
                <p className="text-sm">Vincenti QF1/QF2 e QF3/QF4 (A/R)</p>
                <p className="text-xs text-slate-400 mt-1 font-numbers">A: {fmtD(sfDate.and)} · R: {fmtD(sfDate.rit)}</p>
              </div>
              <div className="p-3 rounded bg-slate-800/40 border border-slate-700/50">
                <p className="text-xs text-slate-400 uppercase mb-1">Finali</p>
                <p className="text-sm">3° / 4° posto · 1° / 2° posto (gara unica)</p>
                <p className="text-xs text-slate-400 mt-1 font-numbers">{fmtD(finaleDate)}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 italic">
              Accoppiamenti fissi per girone (no sorteggio).
            </p>
          </CardContent>
        </Card>

        {!hasKnockout ? (
          <>
          {/* Anteprima Tabellone con i nomi delle squadre attuali */}
          {groupPhaseComplete && (() => {
            const preview = buildPreviewBracket();
            return (
              <Card className="bg-slate-900 border-slate-800 mb-6" data-testid="bracket-preview">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-gold" /> Anteprima Quarti (in base alle classifiche attuali)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {preview.map(qf => (
                      <div key={qf.num} className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
                        <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide flex items-center justify-between">
                          <span>Quarto #{qf.num}</span>
                          <span className="text-slate-400 normal-case">{qf.hint}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0 px-3 py-2 rounded bg-slate-900 truncate font-semibold text-sm">
                            {qf.home?.name || 'TBD'}
                          </div>
                          <span className="text-slate-500 text-xs">vs</span>
                          <div className="flex-1 min-w-0 px-3 py-2 rounded bg-slate-900 truncate font-semibold text-sm text-right">
                            {qf.away?.name || 'TBD'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 italic mt-3">
                    Gli accoppiamenti si aggiornano in tempo reale al variare delle classifiche.
                    Il tabellone definitivo verrà bloccato al termine della fase a gironi.
                  </p>
                </CardContent>
              </Card>
            );
          })()}
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center">
              <Trophy className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h2 className="font-heading text-xl mb-2">Fase eliminatoria non ancora iniziata</h2>
              <p className="text-slate-400 mb-6">
                Il tabellone sarà disponibile al termine della fase a gironi
              </p>
              
              {qualifiedTeams.length > 0 && groupPhaseComplete && (
                <div className="mt-8">
                  <h3 className="font-heading text-lg mb-4">Classifica attuale dei gironi</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {qualifiedTeams.map((q) => (
                      <div key={q.group} className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-sm text-primary font-bold mb-2">Girone {q.group}</div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-5 rounded bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0">1°{q.group}</span>
                            <span className="text-sm truncate">{q.first?.name || '-'}</span>
                            <span className="ml-auto text-xs text-primary/80 font-numbers shrink-0">{q.first?.points || 0}pt</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-5 rounded bg-primary/10 text-primary/80 text-xs font-bold flex items-center justify-center shrink-0">2°{q.group}</span>
                            <span className="text-sm truncate">{q.second?.name || '-'}</span>
                            <span className="ml-auto text-xs text-slate-400 font-numbers shrink-0">{q.second?.points || 0}pt</span>
                          </div>
                          <div className={cn(
                            "flex items-center gap-2 -mx-2 -mb-2 mt-1 px-2 py-1 rounded",
                            q.third && thirds.find(t => t.id === q.third.id)?.qualifies ? "bg-gold/15" : ""
                          )}>
                            <span className={cn(
                              "w-6 h-5 rounded text-xs font-bold flex items-center justify-center shrink-0",
                              q.third && thirds.find(t => t.id === q.third.id)?.qualifies ? "bg-gold/30 text-gold" : "bg-slate-700 text-slate-400"
                            )}>3°{q.group}</span>
                            <span className="text-sm truncate">{q.third?.name || '-'}</span>
                            {q.third && thirds.find(t => t.id === q.third.id)?.qualifies && (
                              <span className="text-[10px] text-gold font-bold whitespace-nowrap">3ª BEST</span>
                            )}
                            <span className="ml-auto text-xs text-slate-400 font-numbers shrink-0">{q.third?.points || 0}pt</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Migliori 3ª (live) */}
                  {thirds.filter(t => t.qualifies).length > 0 && (
                    <div className="mt-6 p-4 bg-gold/10 border border-gold/30 rounded-lg" data-testid="live-best-thirds">
                      <h4 className="font-heading text-base text-gold mb-3 flex items-center gap-2">
                        <Trophy className="w-4 h-4" /> Attuali migliori 3ª (qualificate ai quarti)
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {thirds.filter(t => t.qualifies).map((t, i) => (
                          <div key={t.id} className="flex items-center gap-2 p-2 bg-slate-900/40 rounded">
                            <span className="w-7 h-6 rounded bg-gold/20 text-gold text-xs font-bold flex items-center justify-center shrink-0">3°{t.group_name}</span>
                            <span className="text-sm font-medium truncate">{t.name}</span>
                            <div className="ml-auto flex items-center gap-3 text-xs text-slate-400 font-numbers shrink-0">
                              <span>{t.points || 0}pt</span>
                              <span className={cn("text-[10px] uppercase font-bold", i === 0 ? "text-gold" : "text-gold/80")}>
                                {i === 0 ? '#1 miglior 3ª' : '#2 miglior 3ª'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400 italic mt-2">
                        Ordinamento: punti · differenza reti · gol fatti · fair play
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          </>
        ) : (
          <div className="space-y-8">
            {/* Bracket View */}
            <div className="overflow-x-auto">
              <div className="min-w-[900px] p-4">
                <div className="grid grid-cols-4 gap-8">
                  
                  {/* Quarterfinals */}
                  <div className="space-y-4">
                    <h3 className="font-heading text-lg text-center text-primary mb-4">QUARTI</h3>
                    {Object.entries(qfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="space-y-2">
                        <div className="text-xs text-slate-500 text-center">Q{num}</div>
                        <MatchBracket match={leg1} />
                        <MatchBracket match={leg2} />
                      </div>
                    ))}
                  </div>

                  {/* Semifinals */}
                  <div className="space-y-4 pt-16">
                    <h3 className="font-heading text-lg text-center text-gold mb-4">SEMIFINALI</h3>
                    {Object.entries(sfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="space-y-2">
                        <div className="text-xs text-slate-500 text-center">SF{num}</div>
                        <MatchBracket match={leg1} />
                        <MatchBracket match={leg2} />
                      </div>
                    ))}
                  </div>

                  {/* Final 3rd/4th */}
                  <div className="space-y-4 pt-24">
                    <h3 className="font-heading text-lg text-center text-slate-400 mb-4">3°/4° POSTO</h3>
                    {knockout?.final_3_4 ? (
                      <MatchBracket match={knockout.final_3_4} showLeg={false} />
                    ) : (
                      <div className="bg-slate-900/30 rounded-lg border border-dashed border-slate-700 p-6 text-center text-slate-500">
                        TBD
                      </div>
                    )}
                  </div>

                  {/* Final */}
                  <div className="space-y-4 pt-24">
                    <h3 className="font-heading text-lg text-center mb-4">
                      <span className="text-yellow-400">★</span> FINALE <span className="text-yellow-400">★</span>
                    </h3>
                    {knockout?.final_1_2 ? (
                      <div className="border-2 border-gold/30 rounded-lg p-1">
                        <MatchBracket match={knockout.final_1_2} showLeg={false} />
                      </div>
                    ) : (
                      <div className="bg-slate-900/30 rounded-lg border-2 border-dashed border-gold/30 p-6 text-center text-slate-500">
                        TBD
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>

            {/* Match Details */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quarterfinals Details */}
              {Object.keys(qfGroups).length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="font-heading text-primary">Quarti di Finale</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(qfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="p-3 bg-slate-800/30 rounded-lg">
                        <div className="text-xs text-slate-500 mb-2">Quarto #{num}</div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{leg1?.home_team?.name || 'TBD'}</span>
                          <span className="text-slate-500">vs</span>
                          <span className="font-semibold">{leg1?.away_team?.name || 'TBD'}</span>
                        </div>
                        {leg1?.is_played && leg2?.is_played && (
                          <div className="text-xs text-center mt-2 text-primary">
                            Aggregato: {(leg1.home_score || 0) + (leg2.away_score || 0)} - {(leg1.away_score || 0) + (leg2.home_score || 0)}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Semifinals Details */}
              {Object.keys(sfGroups).length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="font-heading text-gold">Semifinali</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(sfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="p-3 bg-slate-800/30 rounded-lg">
                        <div className="text-xs text-slate-500 mb-2">Semifinale #{num}</div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{leg1?.home_team?.name || 'TBD'}</span>
                          <span className="text-slate-500">vs</span>
                          <span className="font-semibold">{leg1?.away_team?.name || 'TBD'}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TabellonePage;
